import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

const isImage = (mimeType) => typeof mimeType === 'string' && mimeType.startsWith('image/');

export default function COAPage() {
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    api.get('/coa')
      .then(res => { if (active) setCoas(res.data); })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      {/* Hero */}
      <section className="bg-tea-700 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-tea-300 text-sm font-semibold uppercase tracking-widest mb-4">Quality Assurance</p>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">
            Certificate of Analysis Library
          </h1>
          <p className="mt-4 text-tea-200 text-lg max-w-xl leading-relaxed">
            Every Aloha Amino batch is independently tested by accredited third-party laboratories.
            Browse the latest purity reports below.
          </p>
        </div>
      </section>

      {/* What is a COA */}
      <section className="py-14 bg-gray-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586" />
                </svg>
              ),
              title: 'Independent Testing',
              body: 'Each batch is analysed by an accredited third-party laboratory, not an in-house facility. No conflict of interest.',
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5" />
                </svg>
              ),
              title: 'HPLC & Mass-Spec',
              body: 'Purity is measured by high-performance liquid chromatography (HPLC). Identity is confirmed by mass spectrometry. Both results appear on every COA.',
            },
            {
              icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              title: '≥ 98% Guaranteed',
              body: 'We guarantee a minimum 98% purity by HPLC for every product. Batches that fall short are quarantined — they never ship.',
            },
          ].map((item) => (
            <div key={item.title} className="flex gap-4">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-tea-100 text-tea-700">
                {item.icon}
              </span>
              <div>
                <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-[15px] leading-relaxed">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Uploaded COA library */}
      <main id="main-content" tabIndex={-1} className="flex-1 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Batch Certificates of Analysis</h2>
            {coas.length > 0 && (
              <span className="text-sm text-gray-400">{coas.length} document{coas.length === 1 ? '' : 's'}</span>
            )}
          </div>

          {loading && (
            <div className="py-16 text-center text-gray-400">Loading certificates…</div>
          )}

          {!loading && loadError && (
            <div className="py-16 text-center bg-red-50 rounded-2xl border border-red-100">
              <div className="text-4xl mb-3">⚠️</div>
              <p className="text-gray-600 text-sm">We couldn't load the certificates right now. Please try again later.</p>
            </div>
          )}

          {!loading && !loadError && coas.length === 0 && (
            <div className="py-16 text-center bg-gray-50 rounded-2xl border border-gray-200">
              <div className="text-4xl mb-3">📄</div>
              <p className="text-gray-700 font-medium mb-1">No certificates have been published yet.</p>
              <p className="text-gray-500 text-sm">
                Need a COA for a specific batch? Email{' '}
                <a href="mailto:support@alohaamino.com?subject=COA%20Request" className="text-tea-700 underline">
                  support@alohaamino.com
                </a>{' '}
                with the batch code on your vial label.
              </p>
            </div>
          )}

          {!loading && !loadError && coas.length > 0 && (
            <div className="rounded-2xl border border-gray-200 divide-y divide-gray-100 max-h-[640px] overflow-y-auto">
              {coas.map((coa) => (
                <div key={coa.id} className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-tea-100 text-tea-700 text-xl">
                    {isImage(coa.mimeType) ? '🖼️' : '📄'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{coa.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">Uploaded {fmtDate(coa.createdAt)}</p>
                  </div>
                  <a
                    href={coa.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 inline-flex items-center gap-1.5 text-tea-700 font-medium text-sm hover:underline"
                  >
                    View
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                    </svg>
                  </a>
                </div>
              ))}
            </div>
          )}

          <p className="mt-4 text-xs text-gray-400">
            Certificates reflect the batches currently on hand. Batch codes are printed on vial labels.
            For historical batch COAs, email <a href="mailto:support@alohaamino.com" className="underline">support@alohaamino.com</a> with your batch code.
          </p>

          {/* Request fallback */}
          <div className="mt-12 bg-tea-50 rounded-2xl p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <h3 className="font-bold text-gray-900 mb-1">Need a COA for a specific order?</h3>
              <p className="text-gray-600 text-[15px]">
                Email us your order number and the batch code printed on the vial label. We'll reply with the COA PDF within one business day.
              </p>
            </div>
            <a
              href="mailto:support@alohaamino.com?subject=COA%20Request"
              className="btn-primary shrink-0"
            >
              Email Support
            </a>
          </div>

          <div className="mt-8 text-sm">
            <Link to="/compliance" className="text-tea-700 font-medium hover:underline">← Back to Compliance</Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
